import ContainerMaxWidthXl from '@deltek/specification-client/core/Surfaces/ContainerMaxWidthXl'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import AppBar from '@material-ui/core/AppBar'
import Button from '@material-ui/core/Button'
import { Theme } from '@material-ui/core/styles'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Typography from '@material-ui/core/Typography'
import ExpandLessOutlinedIcon from '@material-ui/icons/ExpandLessOutlined'
import ExpandMoreOutlinedIcon from '@material-ui/icons/ExpandMoreOutlined'
import clsx from 'clsx'
import WorkspaceActionBarWidgetContainer from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarWidgetContainer'
import { WorkspaceActionBarWidgetLabelOptions } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarWidgetLabel'
import React from 'react'
import { getNestedObjectPropertyValue } from 'shared/utils/objectUtils'

const transitionDuration = '100ms'

const useStyles = makeStyles((theme: Theme) => ({
    actionBar: {
        background: '#343A3F;',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(0, 0),
        },
        transition: transitionDuration,
    },
    h1Text: {
        ...theme.typography.h1,
        fontSize: 30,
        [theme.breakpoints.up('xs')]: {
            padding: theme.spacing(0, 3),
        },
    },
    outerWidgetGroupWrapper: {
        display: 'inline-flex',
        justifyContent: 'space-between',
        width: '100%',
    },
    rightAlignedWidgetsWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    collapseExpandButton: {
        cursor: 'pointer',
        color: theme.palette.common.white,
        width: 60,
        alignItems: 'center',
        fontSize: pxToRem(18),
        borderRadius: 0,
        transition: transitionDuration,

        '& > .MuiButton-label': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            '& > p': {
                marginTop: -theme.spacing(1),
            },
        },
    },
    collapsedButton: {
        height: 40,
        marginTop: -theme.spacing(0.625),
        marginBottom: -theme.spacing(0.625),
    },
    expandedButton: {
        height: 90,
        marginTop: -theme.spacing(1.25),
        marginBottom: -theme.spacing(1.25),
    },
    collapsedBar: {
        minHeight: 40,
        maxHeight: 40,
        paddingTop: theme.spacing(0.625),
        paddingBottom: theme.spacing(0.625),
    },
    expandedBar: {
        minHeight: 90,
        maxHeight: 90,
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
    },
    expandCollapseButtonLabel: {
        fontSize: pxToRem(18),
        marginTop: -theme.spacing(0.625),
    },
}))

export interface ImportPageActionBarWidget {
    /**
     * Label shown on top of the action bar widget.
     * This is also the portion that remains displayed
     * when the action bar is "collapsed". If undefined,
     * the action bar will display the `component` when
     * collpsed instead.
     *
     * You can directly pass in the text to be displayed,
     * or optionally use the object form to pass in
     * a custom text alignment as well.
     *
     */
    label?: string | WorkspaceActionBarWidgetLabelOptions

    /**
     * Custom component rendered below the label
     */
    component: React.ReactNode

    /**
     * String key used to update the widget's state via the
     * context. No need to pass this in if the widget is
     * stateless.
     *
     * Use this for "counter"-type widgets.
     */
    stateKey?: string

    /* custom style to be applied on the widget container */
    customContainerStyle?: string
}
export interface ImportActionBarActionBarProps {
    left?: ImportPageActionBarWidget[][]
    right?: ImportPageActionBarWidget[][]
    onStateChange: (stateKey: string, newValue: any, persist?: boolean) => void
    currentState: Record<string, any>
}

const ImportPageActionBar: React.FC<ImportActionBarActionBarProps> = ({
    left = [],
    right = [],
    onStateChange,
    currentState,
}) => {
    const classes = useStyles()

    const toggleCollapseState = React.useCallback(
        (stateKey: string) => {
            onStateChange(
                stateKey,
                !getNestedObjectPropertyValue(stateKey, currentState),
                true
            )
        },
        [currentState, onStateChange]
    )

    return (
        <AppBar
            elevation={0}
            position="relative"
            className={clsx(
                'workspaceActionBar',
                classes.actionBar,
                getNestedObjectPropertyValue(
                    'showImportActionBarState.importActionBar',
                    currentState
                )
                    ? classes.collapsedBar
                    : classes.expandedBar
            )}
        >
            <ContainerMaxWidthXl>
                <div className={classes.outerWidgetGroupWrapper}>
                    {
                        <WorkspaceActionBarWidgetContainer
                            showGroupBorder
                            widgets={left}
                            isCollapsed={getNestedObjectPropertyValue(
                                'showImportActionBarState.importActionBar',
                                currentState
                            )}
                        />
                    }

                    <div className={classes.rightAlignedWidgetsWrapper}>
                        {
                            <WorkspaceActionBarWidgetContainer
                                showLeftBorder
                                widgets={right}
                                isCollapsed={getNestedObjectPropertyValue(
                                    'showImportActionBarState.importActionBar',
                                    currentState
                                )}
                            />
                        }
                        <Button
                            variant="contained"
                            disableElevation
                            data-testid="collapseExpandButton"
                            className={clsx(
                                classes.collapseExpandButton,
                                getNestedObjectPropertyValue(
                                    'showImportActionBarState.importActionBar',
                                    currentState
                                )
                                    ? classes.collapsedButton
                                    : classes.expandedButton
                            )}
                            color="secondary"
                            onClick={() =>
                                toggleCollapseState(
                                    'showImportActionBarState.importActionBar'
                                )
                            }
                        >
                            {getNestedObjectPropertyValue(
                                'showImportActionBarState.importActionBar',
                                currentState
                            ) ? (
                                <ExpandMoreOutlinedIcon fontSize="small" />
                            ) : (
                                <ExpandLessOutlinedIcon fontSize="small" />
                            )}
                            <Typography
                                component="span"
                                className={classes.expandCollapseButtonLabel}
                            >
                                {getNestedObjectPropertyValue(
                                    'showImportActionBarState.importActionBar',
                                    currentState
                                )
                                    ? 'Show'
                                    : 'Close'}
                            </Typography>
                        </Button>
                    </div>
                </div>
            </ContainerMaxWidthXl>
        </AppBar>
    )
}

export default ImportPageActionBar
